import { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Lock, ArrowLeft, Loader2, Eye, EyeOff, CheckCircle } from 'lucide-react';
import api from '../services/api';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/auth/reset-password', { token, new_password: password });
      setSuccess(true);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-teal-50 flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <Lock className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-gray-900 mb-2">Invalid Reset Link</h1>
          <p className="text-gray-500 mb-6">This password reset link is invalid or has expired.</p>
          <Link to="/auth" className="text-teal-700 font-medium hover:underline" data-testid="back-to-login-invalid">
            Back to Sign In
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-teal-50 flex items-center justify-center px-6">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md text-center">
          <CheckCircle className="w-16 h-16 text-teal-600 mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-gray-900 mb-2">Password Reset Complete</h1>
          <p className="text-gray-500 mb-6">Your password has been updated. You can now sign in with your new password.</p>
          <Button onClick={() => navigate('/auth')} className="bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-full px-8 h-11 font-semibold" data-testid="go-to-login-btn">
            Sign In
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-teal-50 flex items-center justify-center px-6" data-testid="reset-password-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Link to="/auth" className="flex items-center gap-2 text-teal-700 hover:opacity-80 transition-opacity mb-8" data-testid="back-to-login-link">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Sign In</span>
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <img src="/logo.jpg" alt="Triple A Travel Solutions" className="w-12 h-12 rounded-lg object-contain" />
          <div>
            <span className="font-serif text-xl font-bold text-teal-800 block leading-tight">Triple A</span>
            <span className="text-[10px] font-sans font-medium tracking-widest uppercase text-teal-600">Travel Solutions</span>
          </div>
        </div>

        <h1 className="font-serif text-3xl text-gray-900 mb-2">Set New Password</h1>
        <p className="text-gray-500 mb-6">Enter your new password below.</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="newPassword" className="text-gray-700 text-sm">New Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input id="newPassword" type={showPassword ? 'text' : 'password'} placeholder="Min. 6 characters" value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-9 pr-10 h-11 border-gray-200 focus:border-teal-500 focus:ring-teal-500/20"
                data-testid="new-password-input" />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors" tabIndex={-1}>
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="confirmPassword" className="text-gray-700 text-sm">Confirm Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input id="confirmPassword" type={showPassword ? 'text' : 'password'} placeholder="Re-enter password" value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="pl-9 h-11 border-gray-200 focus:border-teal-500 focus:ring-teal-500/20"
                data-testid="confirm-password-input" />
            </div>
          </div>

          {password && confirmPassword && password !== confirmPassword && (
            <p className="text-red-500 text-xs" data-testid="password-mismatch-error">Passwords do not match</p>
          )}

          <Button type="submit" className="w-full h-11 bg-gradient-to-r from-teal-600 to-teal-700 text-white hover:from-teal-700 hover:to-teal-800 rounded-full font-semibold"
            disabled={loading || !password || password.length < 6 || password !== confirmPassword}
            data-testid="reset-password-submit-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Reset Password'}
          </Button>
        </form>
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
